import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, Shield, BarChart3, Database, Gauge, ArrowLeft,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import { useAuth } from '../context/AuthContext';

import EmailChannelHealthTile from '../components/admin/EmailChannelHealthTile';
import LiteracyHeatmapTile from '../components/admin/LiteracyHeatmapTile';
import FailedLoginActivityTile from '../components/admin/FailedLoginActivityTile';
import TourFunnelTile from '../components/admin/TourFunnelTile';
import ToolkitUsageTile from '../components/admin/ToolkitUsageTile';
import SalesIntelTile from '../components/admin/SalesIntelTile';
import SalesIntelFunnelTile from '../components/admin/SalesIntelFunnelTile';
import WhitepaperDownloadsTile from '../components/admin/WhitepaperDownloadsTile';
import PageFunnelTile from '../components/admin/PageFunnelTile';
import QFixEngagementTile from '../components/admin/QFixEngagementTile';
import WeakLinkDigestTile from '../components/admin/WeakLinkDigestTile';
import PendingInvitesTile from '../components/admin/PendingInvitesTile';
import { NarrativeBanditLeaderboard } from '../components/admin/NarrativeBanditLeaderboard';
import SeedDataTab from '../components/admin/SeedDataTab';
import CollapsibleSection from '../components/layout/CollapsibleSection';
import PageToolbar from '../components/layout/PageToolbar';

/**
 * AdminTelemetryPage — `/admin/telemetry`
 *
 * The "platform observability" half of the split formerly known as
 * the Admin Dashboard. The other half lives in `AdminDashboardPage`
 * and focuses on org / user / role management. Splitting these two
 * concerns lets each page stay scannable: telemetry tiles update
 * frequently and bury management actions that happen rarely, while
 * management actions block on dialogs and don't want a wall of
 * heatmaps competing for attention.
 *
 * Permissions:
 *   • Both Super Admins and Org Admins can land here. Org admins
 *     see only the tiles that natively support org-scoped data
 *     (FailedLoginActivity, PendingInvites, etc.). Inherently
 *     platform-global tiles (BuildInfo, EngineeringHealth, the
 *     Weekly OPS section) are gated to super admins via the
 *     `isSuperAdmin && (...)` checks below — no separate route gate
 *     is required. Backend endpoints powering each tile already
 *     enforce their own scoping, so an org admin who wanders into a
 *     super-admin-only network call gets a 403 and the tile renders
 *     its empty state cleanly.
 *
 * Cross-navigation:
 *   • Hero ships an "← Back to management" button that returns to
 *     `/admin`. The management page mirrors this with a "View
 *     telemetry →" button so admins can hop between the two.
 */
export default function AdminTelemetryPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const theme = getLightSectionTheme('ImpactModelling');

  const heroStats = [
    {
      label: 'Scope',
      value: isSuperAdmin ? 'Platform' : 'Your Org',
      testId: 'telemetry-scope',
    },
  ];

  const heroActions = (
    <div className="flex flex-wrap items-center gap-2">
      <PageToolbar theme={theme} collapsePrefix="telemetry" share={false} testId="telemetry-toolbar" />
      <Button
        onClick={() => navigate('/admin')}
        size="sm"
        variant="ghost"
        className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
        data-testid="telemetry-back-to-admin-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to management
      </Button>
    </div>
  );

  return (
    <PageShell
      theme={theme}
      hero={(
        <PageHero
          title="Platform Telemetry"
          description={
            isSuperAdmin
              ? 'Live engagement, engineering, and content health across the platform.'
              : 'Engagement & security telemetry scoped to your organization.'
          }
          theme={theme}
          icon={Gauge}
          stats={heroStats}
          actions={heroActions}
          testId="telemetry-hero"
        />
      )}
    >
      <div className="px-4 md:px-8 lg:px-12 py-6" data-testid="admin-telemetry-page">
        {/* Status row — actionable telemetry that org admins should
            see (mirrored from the management page so admins who jump
            here don't have to bounce back to triage). */}
        <section className="mb-6" data-testid="telemetry-status-row">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <PendingInvitesTile />
            <FailedLoginActivityTile />
            <EmailChannelHealthTile isSuperAdmin={isSuperAdmin} />
          </div>
        </section>

        {/* Engagement section — what users are doing with the app.
            Defaults expanded because it's the most common reason
            admins land on this page. */}
        <CollapsibleSection
          storageKey="telemetry.engagement"
          eyebrow="Engagement"
          title="Tour, toolkit & content usage"
          accent="text-indigo-600"
          defaultExpanded
          testId="telemetry-engagement-section"
          meta={<span className="text-[11px] text-slate-500">6 tiles</span>}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <LiteracyHeatmapTile />
            <ToolkitUsageTile />
            <TourFunnelTile />
            <PageFunnelTile />
            <WhitepaperDownloadsTile />
            <QFixEngagementTile />
          </div>
        </CollapsibleSection>

        {/* Sales Intel section — only relevant for super admins / SEs. */}
        {isSuperAdmin && (
          <CollapsibleSection
            storageKey="telemetry.sales"
            eyebrow="Sales Intelligence"
            title="Funnel & per-SE digests"
            accent="text-indigo-600"
            testId="telemetry-sales-section"
            meta={<span className="text-[11px] text-slate-500">2 tiles</span>}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SalesIntelFunnelTile />
              <SalesIntelTile />
            </div>
          </CollapsibleSection>
        )}

        {/* Engineering / build info — moved to its own
            top-level Code Health page (`/admin/code-health`) so
            engineering / Ops have a single page covering what's
            shipped + what's failing + what's running heavy. */}

        {/* Weekly OPS — manual digest triggers, super-admin only. */}
        {isSuperAdmin && (
          <CollapsibleSection
            storageKey="telemetry.weeklyOps"
            eyebrow="Weekly OPS"
            title="Manual digest triggers"
            accent="text-rose-600"
            testId="admin-weekly-ops-section"
            meta={<span className="text-[11px] text-slate-500">Preview · Send Today</span>}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <WeakLinkDigestTile />
            </div>
          </CollapsibleSection>
        )}

        {/* Deep-dive tabs — Narrative / Seed Data live here (rather
            than mixed in with Users / Orgs / Roles) because they're
            observability concerns, not management. Code Health was
            split out to its own top-level nav entry (`/admin/code-health`)
            so engineering / Ops can land directly on the dashboard
            without walking past unrelated telemetry tiles. */}
        {isSuperAdmin && (
          <Tabs defaultValue="narrative-bandit" className="mt-8">
            <TabsList className="grid grid-cols-2 w-full max-w-xl mb-6">
              <TabsTrigger value="narrative-bandit" className="flex items-center gap-2" data-testid="telemetry-tab-bandit">
                <BarChart3 size={16} />
                Narrative
              </TabsTrigger>
              <TabsTrigger value="seed-data" className="flex items-center gap-2" data-testid="telemetry-tab-seed">
                <Database size={16} />
                Seed Data
              </TabsTrigger>
            </TabsList>

            <TabsContent value="narrative-bandit">
              <NarrativeBanditLeaderboard />
            </TabsContent>
            <TabsContent value="seed-data">
              <SeedDataTab />
            </TabsContent>
          </Tabs>
        )}

        {!isSuperAdmin && (
          <div
            className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600"
            data-testid="telemetry-org-admin-note"
          >
            <p className="flex items-center gap-2 font-medium text-slate-700">
              <Activity size={14} /> Showing telemetry scoped to your organization.
            </p>
            <p className="mt-1 text-xs">
              Platform-wide engineering, build, and code-health views are reserved for Super Admins.
            </p>
          </div>
        )}
      </div>
    </PageShell>
  );
}
